<template>
  <div>
    <div class="bg-grey-300 mb-2 h-[42px]" />
    <div class="shadow-pm rounded-xl">
      <div id="summary-chart" class="relative rounded-t-xl bg-white px-6 pb-8">
        <div
          v-if="slug !== SYNTHETIC_GRAPHITE.slug ? title : altTitle"
          class="mb-6 mt-1 flex w-full flex-row items-center justify-center font-bold lg:text-xl"
        >
          {{ slug !== SYNTHETIC_GRAPHITE.slug ? title : altTitle }}
        </div>
        <div
          id="legend-container"
          class="mx-4 mb-5 mt-2.5 flex flex-col items-end justify-center gap-y-4 md:mx-0 md:mb-9 md:flex-row md:items-start md:justify-start md:gap-x-6"
        />
        <div class="relative z-10 min-h-[400px] w-full">
          <PriceAssessmentsLineChart
            v-if="datacollection.datasets.length > 0"
            :chart-data="datacollection"
            :plugins="plugins"
            :width="400"
          />
        </div>

        <div class="mt-4 text-right text-xs font-normal">*Index Values</div>

        <BenchmarkWatermark />
      </div>
      <div class="block overflow-x-auto rounded-b-xl bg-white">
        <PriceAssessmentsSummaryTableIndex
          data-testid="index-table"
          :slug="slug"
          :summary="tableSummary"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import CrosshairPlugin from 'chartjs-plugin-crosshair'
import PriceAssessmentsLineChart from '~/components/price-assessments/LineChart.vue'
import { generateSimpleLegends } from '~/utils/chart/customLegends/simpleLegends'
import PriceAssessmentsSummaryTableIndex from '~/components/price-assessments/SummaryTableIndex.vue'
import type { TGeneral } from '~/src/types/common'
import { SYNTHETIC_GRAPHITE } from '~/constants/services/constants'

const { $rudderstack } = useNuxtApp()
const props = withDefaults(
  defineProps<{
    summary: TGeneral
    slug: string
    tableSummary: TGeneral[]
    title?: string | null
    altTitle?: string | null
  }>(),
  {
    title: null,
    altTitle: null,
  },
)

const datacollection = ref({
  datasets: [],
  labels: [],
})

const plugins = [generateSimpleLegends, CrosshairPlugin]

const extractLabels = () => {
  const dates: string[] = []

  Object.keys(props.summary).forEach((dataset) => {
    props.summary[dataset].forEach(({ date }: { date: string }) => {
      if (!dates.includes(date)) {
        dates.push(date)
      }
    })
  })

  dates.sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
  datacollection.value.labels = dates
}

const getColorChart = (label: string) => {
  const colors: TGeneral = {
    lithium: '#FDBD00',
    'lithium carbonate': '#FD8900',
    'lithium hydroxide': '#86C6F4',
    anode: '#FDBD00',
    cathode: '#FDBD00',
    ev: '#FD8900',
    portable: '#86C6F4',
    ess: '#8B6CD2',
    lfp: '#FDBD00',
    'ncm high-nickel': '#FD8900',
    'ncm mid-nickel': '#86C6F4',
  }

  return colors[label?.toLowerCase()] || '#FDBD00'
}

const setDataset = (label: string, array: any[], borderColor: string) => {
  const points = array
    .filter((data) => data.value && data.date)
    .map((item) => ({
      x: item.date,
      y: item.value,
    }))

  datacollection.value.datasets.push({
    label,
    data: points,
    borderColor,
    fill: false,
  })
}

const setDatasetsByMineral = (data: Record<string, any[]>) => {
  Object.entries(data).forEach(([name, values]) => {
    const label = name
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
      .replace('Ion', 'ion')

    setDataset(label, values, getColorChart(label))
  })
}

onMounted(() => {
  extractLabels()
  setDatasetsByMineral(props.summary)

  EventBus.$on('index-chart-hover', () => {
    $rudderstack.trackChartInteraction({
      chart_title: props.title,
      chart_type: 'lines',
    })
  })
})
</script>

<style scoped>
.hidden {
  display: none;
}
</style>
